import { AddToCartButton, useCart, useProduct } from '@shopify/hydrogen-react';
import { SelectedOptions } from '@shopify/hydrogen-react/ProductProvider';
import {
  CartLine,
  ImageEdge,
  Product,
  ProductVariant,
} from '@shopify/hydrogen-react/storefront-api-types';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { findVariantByOptions, getOptionsFromVariant, optionsToQueryString } from './functions';
import ProductInfo from './ProductInfo';
import ProductOutOfStock from './ProductOutOfStock';
import ProductTopMeta from './ProductTopMeta';
import Quantity from './Quantity';
import OptionsWrapper from './variations/OptionsWrapper';
import { formatCurrency } from '../../common/formatCurrencies';
import OptimizedImage from '../../components/ui/OptimizedImage';
import InlineActions from '../app/header/InlineActions';
import { useShowInlineActions } from '../app/header/useInlineActions';
import { ORG_CONFIGS } from '../../config/storeConfig';
import { useStoreState } from '../../store';
import { elevarAddToCart, elevarViewItem } from '../../utils/elevar';
import { sendToParent } from '../../utils/iframe/postMessage';
import { flatResponseEdges } from '../../utils/storefront/flatResponseEdges';
import willReachMaxQuantity from '../../utils/storefront/variantQuantity';

const ProductDetails: React.FC = () => {
  const [queryParamsObj, setQueryParamsObj] = useState<{ [key: string]: string }>({});
  const { product, selectedVariant, setSelectedOptions, selectedOptions, setSelectedVariant } =
    useProduct();
  const { status } = useCart();
  const [selectedProductOptions, setProductSelectedOptions] = useState<SelectedOptions>(
    selectedOptions as SelectedOptions
  );
  const [quantity, setQuantity] = useState<number>(1);
  const [justAdded, setJustAdded] = useState<boolean>(false);
  const addedTimerRef = useRef<number | undefined>(undefined);
  const [totalPrice, setTotalPrice] = useState<number>(+(selectedVariant?.price?.amount || 1));
  const [activeImageUrl, setActiveImageUrl] = useState<string | undefined>(
    selectedVariant?.image?.url || undefined
  );
  const { elevarCustomerData, isElevarInitializationFinished, arrivedFromPage } = useStoreState(
    state => state.analytics
  );
  const showInlineActions = useShowInlineActions();
  const orgId = useStoreState(state => state.orgContext.orgId);
  const bulkOrderUrl = orgId ? ORG_CONFIGS[orgId]?.bulkOrderUrl : undefined;
  const { id, cartCreate, lines } = useCart();
  const createdCartRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleBulkOrder = () => {
    if (!bulkOrderUrl) return;
    const sent = sendToParent({ type: 'enrichly:bulkOrder', url: bulkOrderUrl });
    if (!sent) {
      window.open(bulkOrderUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOptionsSelect = (options: SelectedOptions) => {
    navigate(
      {
        pathname: location.pathname,
        search: optionsToQueryString(options),
      },
      { replace: true }
    );
  };

  useEffect(() => {
    selectedVariant?.price?.amount && setTotalPrice(+selectedVariant.price.amount * quantity);
  }, [quantity, selectedVariant]);

  // Clear the "Added to cart" reset timer on unmount.
  useEffect(() => () => window.clearTimeout(addedTimerRef.current), []);

  // Adding this quantity would push the cart past the variant's available stock.
  const maxQuantityReached = willReachMaxQuantity(
    lines as CartLine[],
    quantity,
    selectedVariant as ProductVariant
  );

  useEffect(() => {
    if (selectedVariant?.image?.url) {
      setActiveImageUrl(selectedVariant.image.url);
    }
  }, [selectedVariant?.image?.url]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const nextQueryParamsObj: { [key: string]: string } = {};

    queryParams.forEach((value, key) => {
      nextQueryParamsObj[key] = value;
    });

    setQueryParamsObj(nextQueryParamsObj);
  }, [location.search]);

  useEffect(() => {
    if (!selectedProductOptions) {
      return;
    }
    setSelectedOptions(selectedProductOptions);
    setSelectedVariant(findVariantByOptions(product as Product, selectedProductOptions));
  }, [selectedProductOptions]);

  useEffect(() => {
    if (
      !!findVariantByOptions(product as Product, queryParamsObj) &&
      !!Object.keys(queryParamsObj).length
    ) {
      const optionsFromVariant = getOptionsFromVariant(
        findVariantByOptions(product as Product, queryParamsObj) as ProductVariant
      );
      setProductSelectedOptions(optionsFromVariant);
      setSelectedOptions(optionsFromVariant);
      setSelectedVariant(findVariantByOptions(product as Product, queryParamsObj));
    }
  }, [queryParamsObj]);

  useEffect(() => {
    if (isElevarInitializationFinished) {
      elevarViewItem(
        elevarCustomerData,
        product as Product,
        selectedVariant as ProductVariant,
        arrivedFromPage
      );
    }
  }, [isElevarInitializationFinished]);

  const images = flatResponseEdges(product?.images?.edges as ImageEdge[]);

  useEffect(() => {
    if (id || createdCartRef.current) return;
    createdCartRef.current = true;
    cartCreate({});
  }, [id, cartCreate]);

  return (
    <div className="product-single">
      {showInlineActions && <InlineActions />}
      <div className="container-fluid">
        <div className="content-wrapper">
          <div className="page-inner">
            <div className="product-single-inner">
              <div className="product-single-top">
                <div className="row">
                  <div className="col-lg-6 col-sm-12">
                    <ProductTopMeta
                      className="mobile-show"
                      product={product as Product}
                      selectedVariant={selectedVariant as ProductVariant}
                    />
                    <div className="product-single-image">
                      <OptimizedImage
                        key={activeImageUrl}
                        className="product-single-image-item featured"
                        viewportSize={{ desktop: 50, desktopSmall: 50, tablet: 50, mobile: 50 }}
                        regularImage={true}
                        url={activeImageUrl || ''}
                        alt={selectedVariant?.image?.altText || product?.title || undefined}
                      />
                      {images.length > 1 && (
                        <div className="product-single-image-gallery">
                          {images.map((item, key) => (
                            <OptimizedImage
                              className={classNames('product-single-image-item gallery', {
                                active: item.url === activeImageUrl,
                              })}
                              viewportSize={{
                                desktop: 10,
                                desktopSmall: 10,
                                tablet: 15,
                                mobile: 25,
                              }}
                              key={key}
                              regularImage={true}
                              url={item.url}
                              alt={item.altText || undefined}
                              onClick={() => setActiveImageUrl(item.url)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-5 col-lg-offset-1 col-md-6 col-md-offset-0 col-sm-12">
                    <ProductTopMeta
                      className="mobile-hide"
                      product={product as Product}
                      selectedVariant={selectedVariant as ProductVariant}
                    />

                    <OptionsWrapper
                      onSelectedOptions={selectedOptions => handleOptionsSelect(selectedOptions)}
                      selectedOptions={selectedOptions as SelectedOptions}
                      selectedVariant={selectedVariant as ProductVariant}
                      product={product as Product}
                    />

                    {selectedVariant && selectedVariant.availableForSale && (
                      <div className="product-atc">
                        <Quantity
                          currentQuantity={quantity}
                          disabled={!selectedVariant?.availableForSale}
                          maxQuantity={selectedVariant?.quantityAvailable as number}
                          onChange={q => setQuantity(q)}
                          withDebounce={false}
                        />
                        <AddToCartButton
                          disabled={
                            !selectedVariant?.availableForSale ||
                            status === 'updating' ||
                            maxQuantityReached
                          }
                          quantity={quantity}
                          onClick={() => {
                            // Returning `false` cancels hydrogen's internal add
                            // (a bare `return` does not — see BaseButton).
                            if (maxQuantityReached) {
                              return false;
                            }

                            elevarAddToCart(
                              elevarCustomerData,
                              product as Product,
                              selectedVariant as ProductVariant,
                              quantity,
                              undefined,
                              arrivedFromPage
                            );

                            window.clearTimeout(addedTimerRef.current);
                            setJustAdded(true);
                            addedTimerRef.current = window.setTimeout(
                              () => setJustAdded(false),
                              5000
                            );
                          }}
                          variantId={selectedVariant.id}
                          className={classNames({
                            'button primary product-atc-button': true,
                            'product-atc-button--added': justAdded,
                            disabled:
                              !selectedVariant?.availableForSale ||
                              status === 'updating' ||
                              maxQuantityReached,
                          })}
                        >
                          {justAdded ? (
                            <span>Added to cart</span>
                          ) : (
                            <>
                              <span>Add to cart</span>
                              <span>|</span>
                              <span>{formatCurrency(totalPrice)}</span>
                            </>
                          )}
                        </AddToCartButton>
                      </div>
                    )}

                    {bulkOrderUrl && (
                      <button
                        type="button"
                        className="app-header__bulk-order"
                        onClick={handleBulkOrder}
                      >
                        Bulk Order
                      </button>
                    )}

                    {product?.descriptionHtml && (
                      <div
                        className="product-single-description"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(product?.descriptionHtml),
                        }}
                      ></div>
                    )}

                    {selectedVariant && <ProductInfo variant={selectedVariant as ProductVariant} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
